import { useState, useContext, useEffect, createContext } from 'react'
import { workspaceAdapter } from '../adapter';
import ErrorBoundary from '../components/error/ErrorBoundary';
import useDataClient from '../hooks/useDataClient';
import {
  CredentialType,
  NewCredentialType,
  WorkspaceContextType,
  RepositoryType,
  BasicProviderProps,
  WorkspaceContextFormType
} from '../types';
import { AppContext } from './AppContext';
import { DashboardContext } from './DashboardContext';

export const WorkspaceContext = createContext<WorkspaceContextType | null>(null);

export default function WorkspaceProvider({ children }: BasicProviderProps) {
  const appContext = useContext(AppContext)
  const dashboardContext = useContext(DashboardContext)
  const credentialDeleteClient = useDataClient<number>({ appContext })
  const repositoryDeleteClient = useDataClient<number>({ appContext })
  const controlCredentialClient = useDataClient<CredentialType>({ appContext })
  const controlRepositoryClient = useDataClient<RepositoryType>({ appContext })

  const [credentialTarget, setCredentialTarget] = useState<CredentialType | null>(null)
  const [repositoryTarget, setRepositoryTarget] = useState<RepositoryType | null>(null)
  const [showForm, setShowForm] = useState<WorkspaceContextFormType | null>(null)

  function updateCredential(data: NewCredentialType) {
    controlCredentialClient.executeRequest(credentialTarget ?
      workspaceAdapter.editCredential(data) :
      workspaceAdapter.addCredential(data)
    )
  }

  function reloadResources(message: string) {
    dashboardContext?.updateAppData('credentials')
    appContext?.setAppDialog({ message: message, type: 'success', onClose: () => setShowForm(null) })
  }

  function confirmCredentialDelete(data: CredentialType) {
    appContext?.setAppDialog({
      message: `¿Desea eliminar la credencial ${data.workspace}? <br/>
      Esto eliminara toda información de los repositorios y recursos registrados en este, pero no afectara lo archivos almacenados en el mismo`,
      type: 'warning',
      onAccept: () => {
        credentialDeleteClient.executeRequest(workspaceAdapter.deleteCredential(data))
      }
    })
  }

  function confirmRepositoryDelete(data: RepositoryType) {
    appContext?.setAppDialog({
      title: 'Aviso',
      message: `¿Desea eliminar el registro del repositorio ${data.name}? <br/>
      Esto eliminara toda información de los recursos registrados en este, pero no afectara lo archivos almacenados en el repositorio`,
      type: 'warning',
      onAccept: () => {
        repositoryDeleteClient.executeRequest(workspaceAdapter.deleteRepository(data))
      }
    })
  }

  function updateRepository(data: RepositoryType, newRepo: boolean) {
    controlRepositoryClient.executeRequest(workspaceAdapter.updateRepository(data, newRepo))
  }

  useEffect(() => {
    let requestAddedId = controlCredentialClient.status.data?.id
    if (requestAddedId) {
      reloadResources(credentialTarget ? `Credencial actualizada` : `Credencial agregada con id ${requestAddedId}`)
    }
  }, [controlCredentialClient.status.data])

  useEffect(() => {
    let requestAddedId = controlRepositoryClient.status.data?.id
    if (requestAddedId) {
      reloadResources(repositoryTarget ? `Repositorio actualizado` : `Repositorio registrado con id ${requestAddedId}`)
    }
  }, [controlRepositoryClient.status.data])

  useEffect(() => {
    if (credentialDeleteClient.status.data) {
      reloadResources('Credencial eliminada')
    }
  }, [credentialDeleteClient.status.data])

  useEffect(() => {
    if (repositoryDeleteClient.status.data) {
      reloadResources('Repositorio eliminado')
    }
  }, [repositoryDeleteClient.status.data])

  useEffect(() => {
    if(!showForm){
      setCredentialTarget(null)
      setRepositoryTarget(null)
    }
  }, [showForm])

  return <ErrorBoundary>
    <WorkspaceContext.Provider
      value={{
        credentialTarget,
        setCredentialTarget,
        updateCredential,
        confirmCredentialDelete,
        confirmRepositoryDelete,
        idCredentialDeleted: credentialDeleteClient.status.data || null,
        repositoryTarget,
        setRepositoryTarget,
        updateRepository,
        showForm,
        setShowForm
      }}>
      {children}
    </WorkspaceContext.Provider>
  </ErrorBoundary>
}
